import { Link } from "react-router-dom";
import type { Chapter } from "../api";
import { BackIcon, ForwardIcon } from "../components/icons";
import { useT } from "../i18n";
import { AUTO_PLAY_COUNTDOWN_MS } from "./autoPlay";
import { neighbours } from "./chapters";

/**
 * ChapterNav is the end of a chapter: the way on to the next, and back to the
 * one before, without a trip through the book page.
 *
 * Next is the filled one: finishing a chapter and going on is what the end of
 * one is for. With Auto-play next chapter on, it fills as the countdown runs
 * (ADR-0177); it only draws it — the rule and the clock are the player's
 * (useAutoPlay.ts), and a tap during it goes on at once.
 */
export default function ChapterNav({
  chapters,
  chapterID,
  countdown,
  onGoNow,
}: {
  chapters: Chapter[];
  chapterID: string;
  /** A countdown under way: seconds left. */
  countdown?: { secondsLeft: number } | null;
  /** Goes on now, playing; Next calls it while a countdown runs. */
  onGoNow?: () => void;
}) {
  const t = useT();
  const { previous, next } = neighbours(chapters, chapterID);
  if (!previous && !next) return null;
  return (
    <nav aria-label={t.reader.chapterNav} data-testid="chapter-nav" className="mt-10 flex gap-3">
      {previous && (
        <Link
          to={`/read/${previous.id}`}
          data-testid="chapter-previous"
          className="flex min-w-0 flex-1 items-center gap-2 rounded-card border border-line px-3 py-3 transition-colors hover:bg-sunk"
        >
          <BackIcon className="size-4 shrink-0 text-muted" />
          <span className="min-w-0">
            <span className="block text-xs text-muted">{t.reader.previousChapter}</span>
            {/* Its own direction, and nothing inside it with one. */}
            <span dir="auto" className="block truncate text-sm font-medium">
              {previous.title}
            </span>
          </span>
        </Link>
      )}
      {next && (
        <Link
          to={`/read/${next.id}`}
          data-testid="chapter-next"
          onClick={(e) => {
            if (!countdown || !onGoNow) return;
            e.preventDefault();
            onGoNow();
          }}
          className="relative flex min-w-0 flex-1 items-center gap-2 overflow-hidden rounded-card bg-sage px-4 py-3 text-on-sage transition-colors hover:bg-sage-deep"
        >
          {/* From the reading edge, as a line is read. A CSS animation, so
              45 seconds of fill re-render nothing. */}
          {countdown && (
            <span
              aria-hidden="true"
              data-testid="autoplay-countdown"
              className="absolute inset-y-0 start-0 bg-sage-deep"
              style={{ animation: `autoplay-fill ${AUTO_PLAY_COUNTDOWN_MS}ms linear forwards` }}
            />
          )}
          <span className="relative min-w-0 flex-1">
            <span className="block text-xs opacity-85">
              {countdown ? t.reader.nextChapterIn(countdown.secondsLeft) : t.reader.nextChapter}
            </span>
            <span dir="auto" className="block truncate text-sm font-semibold">
              {next.title}
            </span>
          </span>
          <ForwardIcon className="relative size-4 shrink-0" />
        </Link>
      )}
    </nav>
  );
}
